import { useState } from 'react';
import { useLayoutContext } from 'context/LayoutContext';
import theme from 'theme';

const MIN_ROW_HEIGHT = 28;
const ROW_RESIZE_HANDLE_SIZE = 20;
const ROW_RESIZE_LINE_HEIGHT = 3;

interface RowResizeHandleProps {
  defaultHeight?: number;
  leftOffset?: number;
  onResize: (rowNumber: number, height: number) => void;
  rowNumber: number;
}

const applyHeightToTableRow = (row: HTMLTableRowElement, newHeight: number) => {
  // Keep the live drag visible before React persists the row height.
  const rowStyle = row.style;
  rowStyle.height = `${newHeight}px`;
  rowStyle.minHeight = `${newHeight}px`;
  rowStyle.maxHeight = `${newHeight}px`;
  Array.from(row.children).forEach(el => {
    const cell = el as HTMLElement;
    cell.style.height = `${newHeight}px`;
    cell.style.minHeight = `${newHeight}px`;
    cell.style.maxHeight = `${newHeight}px`;
  });
};

const getTableRowIndex = (row: HTMLTableRowElement) => {
  const rowGroup = row.parentElement;
  return rowGroup ? Array.from(rowGroup.children).indexOf(row) : -1;
};

const getSpreadsheetResizeScope = (sourceCell: HTMLElement) => {
  const nearestSpreadsheetNode = sourceCell.closest('[id^="spreadsheet-table__"]') as HTMLElement | null;
  const parentSpreadsheetNode = nearestSpreadsheetNode?.parentElement;

  // Row handles live in the title column; resize the parent grid so content rows move during drag too.
  if (parentSpreadsheetNode?.id.startsWith('spreadsheet-table__')) {
    return parentSpreadsheetNode;
  }

  return nearestSpreadsheetNode ?? sourceCell.closest('table');
};

const getMinimumRowHeight = (defaultHeight?: number) => Math.max(MIN_ROW_HEIGHT, defaultHeight ?? 0);

const updateSpreadsheetRowsByLegendRow = (sourceCell: HTMLElement, newHeight: number) => {
  const sourceRow = sourceCell.closest('tr') as HTMLTableRowElement | null;
  if (!sourceRow) {
    return;
  }

  const rowIndex = getTableRowIndex(sourceRow);
  if (rowIndex < 0) {
    return;
  }

  const resizeScope = getSpreadsheetResizeScope(sourceCell);
  const rows = resizeScope?.querySelectorAll<HTMLTableRowElement>(`.data-grid tbody tr:nth-child(${rowIndex + 1})`);

  rows?.forEach(row => applyHeightToTableRow(row, newHeight));
};

const RowResizeHandle = ({ defaultHeight, leftOffset = 0, onResize, rowNumber }: RowResizeHandleProps) => {
  const { isExportMode } = useLayoutContext();
  const [isHovered, setIsHovered] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  if (isExportMode) {
    return null;
  }

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const cell = (event.currentTarget.closest('td') || event.currentTarget.closest('th')) as HTMLElement | null;
    if (!cell) {
      return;
    }

    const ownerWindow = event.currentTarget.ownerDocument.defaultView ?? window;
    const startY = event.clientY;
    const startHeight = cell.offsetHeight;
    const minimumHeight = getMinimumRowHeight(defaultHeight);
    let currentHeight = startHeight;
    let hasMoved = false;
    setIsResizing(true);

    const handleMouseMove = (e: MouseEvent) => {
      hasMoved = true;
      const delta = e.clientY - startY;
      const requestedHeight = startHeight + delta;
      if (requestedHeight < minimumHeight) {
        currentHeight = minimumHeight;
        updateSpreadsheetRowsByLegendRow(cell, minimumHeight);
        return;
      }

      currentHeight = requestedHeight;
      updateSpreadsheetRowsByLegendRow(cell, requestedHeight);
    };

    const handleMouseUp = () => {
      ownerWindow.removeEventListener('mousemove', handleMouseMove);
      ownerWindow.removeEventListener('mouseup', handleMouseUp);
      setIsResizing(false);

      if (hasMoved && currentHeight !== startHeight) {
        onResize(rowNumber, currentHeight);
      }
    };

    ownerWindow.addEventListener('mousemove', handleMouseMove);
    ownerWindow.addEventListener('mouseup', handleMouseUp);
  };

  const handleDoubleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (defaultHeight == null) {
      return;
    }

    const cell = (event.currentTarget.closest('td') || event.currentTarget.closest('th')) as HTMLElement | null;
    if (!cell) {
      return;
    }

    updateSpreadsheetRowsByLegendRow(cell, defaultHeight);
    onResize(rowNumber, defaultHeight);
  };

  return (
    <button
      aria-label={`Resize row ${rowNumber}`}
      onDoubleClick={handleDoubleClick}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      type="button"
      style={{
        alignItems: 'center',
        background: 'transparent',
        border: 0,
        bottom: 0,
        cursor: 'row-resize',
        display: 'flex',
        height: `${ROW_RESIZE_HANDLE_SIZE}px`,
        justifyContent: 'center',
        left: leftOffset ? `-${leftOffset}px` : 0,
        overflow: 'visible',
        padding: 0,
        position: 'absolute',
        transform: 'translateY(50%)',
        touchAction: 'none',
        width: leftOffset ? `calc(100% + ${leftOffset}px)` : '100%',
        zIndex: 3,
      }}>
      <div
        style={{
          backgroundColor: isHovered || isResizing ? theme.palette.primary.main : 'transparent',
          borderRadius: '1px',
          height: `${ROW_RESIZE_LINE_HEIGHT}px`,
          left: 0,
          position: 'absolute',
          right: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          transition: 'background-color 0.15s ease',
        }}
      />
    </button>
  );
};

export default RowResizeHandle;
